import React, { useEffect, useRef, useState } from 'react';

/**
 * Single-row infinite marquee of module circles. Each item is a colored
 * gradient rounded circle with the module icon inside and its label below.
 * The row auto-scrolls left forever; hovering pauses the scroll and lifts
 * the focused circle.
 *
 * The DOM contains the modules twice in sequence — when translation reaches
 * -50%, the second copy is in the exact position of the first, so the loop
 * is seamless.
 *
 * Props:
 *   modules: [{ id, label, description, icon (lucide component), accent (hex) }]
 *   speed: pixels-per-second scroll speed (default 40)
 *   size: circle diameter in px (default 96)
 */
const ModuleGrid = ({ modules, speed = 40, size = 96 }) => {
  const trackRef = useRef(null);
  const rafRef = useRef(0);
  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);
  const lastTsRef = useRef(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      // Track holds two copies — half its scrollWidth is one full sequence.
      halfWidthRef.current = el.scrollWidth / 2;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      if (!paused) {
        offsetRef.current -= speed * dt;
        if (Math.abs(offsetRef.current) >= halfWidthRef.current) {
          offsetRef.current += halfWidthRef.current;
        }
        el.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [paused, speed]);

  const items = [...modules, ...modules]; // duplicated for seamless loop

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => { setPaused(false); setHovered(null); }}
      style={{
        maskImage: 'linear-gradient(90deg, transparent 0, black 6%, black 94%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(90deg, transparent 0, black 6%, black 94%, transparent 100%)',
      }}
    >
      <div
        ref={trackRef}
        className="flex items-start gap-8 md:gap-12 will-change-transform"
        style={{ padding: '32px 0' }}
      >
        {items.map((m, i) => {
          const Icon = m.icon;
          const accent = m.accent || '#3b82f6';
          const key = `${m.id}-${i}`;
          const isHovered = hovered === key;

          return (
            <button
              key={key}
              type="button"
              onMouseEnter={() => setHovered(key)}
              onFocus={() => setHovered(key)}
              onBlur={() => setHovered(null)}
              className="group relative flex flex-col items-center shrink-0 focus:outline-none"
              style={{ width: size + 40 }}
              aria-label={m.label}
            >
              {/* Pulsing ring on hover */}
              {isHovered && (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 rounded-full animate-ping"
                  style={{
                    width: size,
                    height: size,
                    background: accent,
                    opacity: 0.35,
                  }}
                  aria-hidden="true"
                />
              )}

              {/* The circle */}
              <div
                className="relative flex items-center justify-center rounded-full transition-all duration-300"
                style={{
                  width: size,
                  height: size,
                  background: `linear-gradient(135deg, ${accent} 0%, ${accent}dd 55%, ${shade(accent, -20)} 100%)`,
                  boxShadow: isHovered
                    ? `0 20px 40px -10px ${accent}88, 0 0 0 6px ${accent}22`
                    : `0 10px 24px -8px ${accent}55`,
                  transform: isHovered ? 'scale(1.12) translateY(-6px)' : 'scale(1)',
                }}
              >
                {/* Glossy top highlight */}
                <span
                  className="pointer-events-none absolute inset-x-3 top-2 h-1/3 rounded-full opacity-40"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.75), transparent)' }}
                  aria-hidden="true"
                />
                {Icon && <Icon className="relative h-9 w-9 text-white" strokeWidth={2} />}
              </div>

              {/* Label */}
              <div
                className={`mt-4 text-center text-xs font-bold leading-tight transition-all duration-300 ${
                  isHovered ? 'text-slate-900' : 'text-slate-600'
                }`}
                style={{ width: size + 20 }}
              >
                {m.label}
              </div>

              {/* Description reveal on hover */}
              <div
                className={`mt-1 text-center text-[10px] leading-snug text-slate-500 transition-all duration-300 ${
                  isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 h-0'
                }`}
                style={{ width: size + 40 }}
              >
                {m.description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Cheap hex-color shade — takes '#rrggbb' and shifts each channel by `pct`
 * (negative darkens). Falls back to input if not a valid hex.
 */
function shade(hex, pct) {
  const m = /^#([a-f0-9]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const r = clamp(((n >> 16) & 0xff) + Math.round(255 * (pct / 100)));
  const g = clamp(((n >> 8) & 0xff) + Math.round(255 * (pct / 100)));
  const b = clamp((n & 0xff) + Math.round(255 * (pct / 100)));
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

export default ModuleGrid;
